import { useTranslation } from 'next-i18next';
import React, { useState } from 'react';
import styled from 'styled-components';
import { useDeviceType } from '@hotelplan/libs.context.device-type';
import { FeatureTooltipsProvider } from '@hotelplan/libs.context.feature-tooltips';
import { formatDate, parseDateStr } from '@hotelplan/libs.utils';
import { FdrProductOfferStatus } from '@hotelplan/supergraph-api';
import { sx2CssThemeFn } from '@hotelplan/util.theme.sxc';
import EditableContainer from 'components/domain/editable-container/EditableContainer';
import WishlistItemEditModal from 'components/domain/wishlist/WishlistItemEditModal';
import { useWishlistContentScrollContext } from 'components/domain/wishlist/WishlistScrollableContext';
import useRemoveOfferFromActiveWishlist from 'fdr/components/candidate/fdr-wishlist/fdr-use-remove-offer-from-active-wishlist';
import { useWishlistSelectedItemsContext } from 'fdr/components/candidate/fdr-wishlist/fdr-wishlist-selected-items-context';
import {
  isFdrWishlistProduct,
  isFdrWishlistProductHotel,
} from 'fdr/components/candidate/fdr-wishlist/fdr-wishlist.types';
import { FdrOfferItem } from 'fdr/components/local/fdr-offer';
import { getFdrProductBreadcrumbs } from 'fdr/components/local/fdr-page-components/recommendations/product/fdr-product-recommendation.utils';
import { getFdrProductIcons } from 'fdr/components/local/fdr-travel-types/fdr-travel-type.utils';
import { FdrWishlistItemFragment } from 'fdr/schemas/fragment/wishlist/fdr-wishlist.generated';
import { useFdrWishlistMoveWishlistItemToWishlistMutation } from 'fdr/schemas/mutation/wishlist/fdr-wishlist-move-wishlist-item-to-wishlist.generated';
import { FdrGetActiveWishlistDocument } from 'fdr/schemas/query/wishlist/fdr-get-active-wishlist.generated';
import { FdrWishlistOverviewDocument } from 'fdr/schemas/query/wishlist/fdr-wishlist-overview.generated';
import FdrWishlistItemPriceChangesDisrupter from './fdr-wishlist-item-price-changes-disrupter';
import { WISHLIST_OFFER_DATE_FORMAT } from './fdr-wishlist.constants';
import { getOfferLinkFromWishlistItem } from './fdr-wishlist.utils';

const OfferEditableContainer = styled(EditableContainer)(
  sx2CssThemeFn({
    '.edit-button': {
      top: ['160px', '15px'],
      right: '15px',
    },
  })
);

const OfferItemWrapper = styled(FdrOfferItem)(
  sx2CssThemeFn({
    mb: [4, '20px'],
  })
);

type WishlistItem = FdrWishlistItemFragment;

const FdrWishlistListViewContent: React.FC = () => {
  const { mobile } = useDeviceType();
  const [t] = useTranslation(['common', 'wishlist']);
  const [moveToWishlist] = useFdrWishlistMoveWishlistItemToWishlistMutation({
    refetchQueries: [FdrWishlistOverviewDocument, FdrGetActiveWishlistDocument],
  });
  const { listRef } = useWishlistContentScrollContext();
  const [removeFromActiveWishlist] = useRemoveOfferFromActiveWishlist();
  const { items, selectedItems, saveAll } = useWishlistSelectedItemsContext();

  const [wishlistItem, setWishlistItem] = useState<WishlistItem | null>(null);

  const onUpdate = () => {
    let notSelectedItemIndex = 0;
    const notSelectedItems =
      items?.filter(
        item =>
          selectedItems.findIndex(it => it?.product.id === item.product.id) ===
          -1
      ) || [];

    saveAll(
      selectedItems.map(item => {
        if (!item || item.product.id !== wishlistItem?.product.id) return item;

        if (
          notSelectedItems.length === 0 ||
          !notSelectedItems[notSelectedItemIndex]
        )
          return null;

        return notSelectedItems[notSelectedItemIndex++];
      })
    );
  };

  return (
    <div ref={listRef}>
      <FeatureTooltipsProvider preventContainerOverlapping={mobile}>
        {items?.map(item => {
          const link = getOfferLinkFromWishlistItem(item);
          const offer = item.trackedOffer;
          const product = item.product;

          if (!isFdrWishlistProduct(product)) {
            return;
          }

          const offerToShow = offer.latest ? offer.latest : offer.original;

          return (
            <React.Fragment key={`p-${product.id}`}>
              {(offer.status === FdrProductOfferStatus.PriceChanged ||
                offer.status ===
                  FdrProductOfferStatus.FlightAndPriceChanged) && (
                <FdrWishlistItemPriceChangesDisrupter
                  className="price-changes-disrupter"
                  item={item}
                />
              )}
              <OfferEditableContainer
                tooltipText={t('wishlist:editIcon.tooltip')}
                key={`wishlist-item-id-${product.id}`}
                onEdit={() => setWishlistItem(item)}
              >
                <OfferItemWrapper
                  title={product.name}
                  productType={product.__typename}
                  image={product.hero}
                  breadcrumbs={getFdrProductBreadcrumbs(product)}
                  rating={
                    isFdrWishlistProductHotel(product)
                      ? product.rating
                      : undefined
                  }
                  productFeatures={
                    isFdrWishlistProductHotel(product)
                      ? product.summaryFeatureGroups
                      : undefined
                  }
                  link={link}
                  price={offerToShow.pricePerPerson}
                  productTravelType={getFdrProductIcons(
                    offerToShow.travelType,
                    product.__typename
                  )}
                  departureDate={formatDate(
                    parseDateStr(offerToShow.departureDate),
                    WISHLIST_OFFER_DATE_FORMAT
                  )}
                  returnDate={formatDate(
                    parseDateStr(offerToShow.returnDate),
                    WISHLIST_OFFER_DATE_FORMAT
                  )}
                  duration={t('common:nights.count', {
                    count: offerToShow.duration,
                  })}
                  mealType={offerToShow.rooms
                    .map(r => r.mealDescription)
                    .filter(Boolean)
                    .join(',')}
                  transferPrice={offerToShow.transferPrice}
                  transferIncluded={offerToShow.transferIncluded}
                  testId={'wishlist-list-view-item'}
                />
              </OfferEditableContainer>
            </React.Fragment>
          );
        })}
      </FeatureTooltipsProvider>
      {wishlistItem && (
        <WishlistItemEditModal
          onClose={() => setWishlistItem(null)}
          onRemove={async () => {
            await removeFromActiveWishlist(
              wishlistItem.trackedOffer.original.id
            );

            onUpdate();
            setWishlistItem(null);
          }}
          onMove={async wishlistId => {
            await moveToWishlist({
              variables: {
                input: {
                  wishlistId,
                  offerId: wishlistItem.trackedOffer.original.id,
                },
              },
            });

            onUpdate();
            setWishlistItem(null);
          }}
        />
      )}
    </div>
  );
};

export default FdrWishlistListViewContent;
